import { apiFetch, authHeaders } from '@/shared/api/client';
import type { ResumenOperacionesFields } from '@/shared/api/controllers/resumenOperaciones';
import type { BitacorasFields } from '@/shared/api/controllers/bitacoras';

/**
 * Mantenimiento API wrappers. M1 ships the read-side + Bitácoras
 * CRUD; M2 adds Confirmar Despacho; M3 Puesta Marcha; M4 Certificado.
 */

export type MantenimientoProgress = {
  despachado: boolean;
  nroDespachoConfirmado: boolean;
  puestaMarchaCargada: boolean;
  certificadoCargado: boolean;
};

export type MantenimientoRow = {
  id: string;
  fields: ResumenOperacionesFields;
  progress: MantenimientoProgress;
  /** Enriched from ABM_Contactos.Celular_C. Empty string when unknown. */
  telefono: string;
  /** Preferred: ABM_Contactos.Mail_C; falls back to ResumenOperaciones.Correo_RO. */
  correo: string;
};

/**
 * The three statuses the Mantenimiento page exposes. `Pago Realizado`
 * and `Despachado` (legacy transitional statuses from PowerApps) are
 * intentionally excluded — the flow now goes straight from Cobros into
 * `Pendiente Despacho` and from M2 into `Pendiente Puesta Marcha`.
 */
export const MANTENIMIENTO_STATUS_OPTIONS = [
  'Pendiente Despacho',
  'Pendiente Puesta Marcha',
  'Cerrado',
] as const;
export type MantenimientoStatus = (typeof MANTENIMIENTO_STATUS_OPTIONS)[number];

/**
 * Default-visible statuses: excludes `Cerrado` so closed ventas don't
 * clutter the active list. User has to explicitly opt-in to see them.
 */
export const DEFAULT_MANTENIMIENTO_STATUSES: MantenimientoStatus[] = [
  'Pendiente Despacho',
  'Pendiente Puesta Marcha',
];

export type ListMantenimientoParams = {
  search?: string;
  statuses?: MantenimientoStatus[];
};

export type ListMantenimientoResponse = {
  rows: MantenimientoRow[];
  totalRowCount: number;
};

export async function listMantenimiento(
  params: ListMantenimientoParams = {},
): Promise<ListMantenimientoResponse> {
  const query: Record<string, string> = {};
  if (params.search) query.search = params.search;
  if (params.statuses && params.statuses.length > 0) {
    query.status = params.statuses.join(',');
  }
  return apiFetch<ListMantenimientoResponse>('/api/mantenimiento', { query });
}

// --------------------------------------------------------------------------
// Single-venta detail page
// --------------------------------------------------------------------------

export type MantenimientoContacto = {
  nombre: string;
  mail: string;
  cuit: string;
  telefono: string;
  medioContacto: string;
};

export type MantenimientoDetailResponse = {
  id: string;
  fields: ResumenOperacionesFields;
  progress: MantenimientoProgress;
  contacto: MantenimientoContacto;
};

export async function getMantenimientoDetail(
  ventaId: string,
): Promise<MantenimientoDetailResponse> {
  return apiFetch<MantenimientoDetailResponse>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}`,
  );
}

// --------------------------------------------------------------------------
// Compact detalles (cart-icon popup + Num. Serie dialog)
// --------------------------------------------------------------------------

export type MantenimientoDetalleRow = {
  id: string;
  /** SP ABM_Articulos id of the line's article (IDArticulo_DO). */
  articuloId: string;
  articulo: string;
  cantidad: number;
  monto: number;
  subtotal: number;
  subtotalDescontado: number;
  descuento: number;
  /** IVA rate on the line (21/10.5 AR, 16/8 MX). */
  iva: number;
  total: number;
  nroSerie: string;
  nroDespacho: string;
  esRegalo: boolean;
  llevaGarantia: boolean;
  garantiaMeses: number | null;
  /** yyyy-mm-dd: vencimiento (manual al cotizar, o entrega + meses al despachar). */
  garantiaVence: string | null;
  /**
   * Resolved on the server from the article's cuenta contable (cuenta
   * is authoritative; falls back to article's `LlevaNroSerie_ART`).
   * When false, the line skips the serie/despacho slots entirely —
   * Accesorios, Repuestos and similar non-serial items render "—" in
   * the per-line edit dialog and aren't blocked by the missing-numeros
   * gate before Confirmar Despacho.
   */
  llevaNroSerie: boolean;
};

export async function listMantenimientoDetalles(
  ventaId: string,
): Promise<MantenimientoDetalleRow[]> {
  const res = await apiFetch<{ detalles: MantenimientoDetalleRow[] }>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/detalles`,
  );
  return res.detalles;
}

export type UpdateDetalleNumerosPayload = {
  nroSerie: string;
  nroDespacho: string;
};

export async function updateDetalleNumeros(
  ventaId: string,
  detalleId: string,
  payload: UpdateDetalleNumerosPayload,
): Promise<{
  outcome: 'updated';
  detalleId: string;
  nroSerie: string;
  nroDespacho: string;
  /** True when this edit reset the previously-set
   *  `NroDespachoConfirmado_RO` flag on the venta — the admin will
   *  need to re-confirm before "Realizar Despacho" is unlocked. */
  resetConfirmacion: boolean;
}> {
  return apiFetch(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/detalles/${encodeURIComponent(detalleId)}/numeros`,
    { method: 'PUT', body: payload },
  );
}

export type ConfirmarNroDespachoResponse =
  | { outcome: 'confirmed' }
  | { outcome: 'already_confirmed' }
  | { outcome: 'blocked_status'; message: string };

export async function confirmarNroDespacho(
  ventaId: string,
): Promise<ConfirmarNroDespachoResponse> {
  return apiFetch<ConfirmarNroDespachoResponse>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/confirmar-nro-despacho`,
    { method: 'POST' },
  );
}

// --------------------------------------------------------------------------
// Cambiar horno (article swap request → admin approval)
// --------------------------------------------------------------------------

export type CambiarHornoPayload = {
  /** SP DetalleOperaciones id of the horno line to replace. */
  lineId: string;
  /** SP ABM_Articulos id of the replacement horno. */
  newArticleId: string;
  /** Discount % for the new horno (0 = precio de lista). */
  descuentoPct?: number;
  motivo?: string;
};

export type CambiarHornoResponse =
  | { outcome: 'pending_approval_created'; autorizacionId: number; oldTotal: number; newTotal: number }
  | { outcome: 'already_pending'; message: string }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'blocked_nova'; message: string }
  | { outcome: 'stock_insuficiente'; message: string };

/**
 * Request a horno swap on a Pendiente-Despacho venta. Files an approval
 * request (does not change the venta); an admin resolves it from the
 * Aprobaciones queue. The `price_too_low` / `same_article` / validation
 * failures come back as thrown ApiErrors (422/404); the discriminated
 * outcomes above are the 200 business results the dialog branches on.
 */
export async function cambiarHorno(
  ventaId: string,
  payload: CambiarHornoPayload,
): Promise<CambiarHornoResponse> {
  return apiFetch<CambiarHornoResponse>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/cambiar-horno`,
    { method: 'POST', body: payload },
  );
}

// --------------------------------------------------------------------------
// Bitácoras
// --------------------------------------------------------------------------

export type BitacoraPhoto = {
  id: string;
  driveItemId: string | null;
  legacyBase64: string | null;
};

export type BitacoraRow = {
  id: string;
  fields: BitacorasFields;
  photos: BitacoraPhoto[];
};

export async function listBitacorasForVenta(
  ventaId: string,
): Promise<BitacoraRow[]> {
  const res = await apiFetch<{ bitacoras: BitacoraRow[] }>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/bitacoras`,
  );
  return res.bitacoras;
}

export type CreateBitacoraResponse = {
  outcome: 'created';
  bitacoraId: string;
  idUnico: string;
  photoDriveItemId: string | null;
};

/**
 * Multipart upload — uses native FormData + fetch because `apiFetch`
 * serializes as JSON. Attaches the bearer token manually.
 */
export async function createBitacora(args: {
  ventaId: string;
  descripcion: string;
  photo: File | null;
}): Promise<CreateBitacoraResponse> {
  const form = new FormData();
  form.append('descripcion', args.descripcion);
  if (args.photo) form.append('photo', args.photo);
  const res = await fetch(
    `/api/mantenimiento/${encodeURIComponent(args.ventaId)}/bitacoras`,
    {
      method: 'POST',
      body: form,
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Bitácora upload failed: ${res.status} ${text}`);
  }
  return (await res.json()) as CreateBitacoraResponse;
}

/** URL for an inline photo render. The backend streams the file through. */
export function bitacoraPhotoUrl(driveItemId: string): string {
  return `/api/mantenimiento/photo/${encodeURIComponent(driveItemId)}`;
}

export type PhotoMeta = {
  driveItemId: string;
  fileName: string;
  mimeType: string;
  size: number;
};

/**
 * Fetch just the drive-item metadata (filename, mime, size) — no body.
 * Used by the Despacho / Bitácora viewers so we can show real filenames
 * instead of "Comprobante N" placeholders.
 */
export async function fetchPhotoMeta(driveItemId: string): Promise<PhotoMeta> {
  return apiFetch<PhotoMeta>(
    `/api/mantenimiento/photo/${encodeURIComponent(driveItemId)}/meta`,
  );
}

// --------------------------------------------------------------------------
// M2 — Confirmar Despacho
// --------------------------------------------------------------------------

export type DespachoDetalleSummary = {
  id: string;
  articuloId: string;
  articulo: string;
  modelo: string;
  cantidad: number;
  llevaNroSerie: boolean;
  currentSerie: string;
  /** N° Despacho saved on the detalle — set from
   *  `NumerosSerieDespachoDialog`, displayed read-only here. */
  currentDespacho: string;
  articuloInventarioId: string;
  /** yyyy-mm-dd: vencimiento manual ya pasado (null si no aplica). */
  garantiaVencida: string | null;
};

export type DespachoClienteSummary = {
  nombre: string;
  direccionCompleta: string;
  direccion: string;
  mail: string;
};

export type PrepareDespachoResponse = {
  ventaId: string;
  clienteNombre: string;
  despachado: boolean;
  statusRO: string;
  detalles: DespachoDetalleSummary[];
  cliente: DespachoClienteSummary | null;
  suggestedDireccion: string;
  suggestedRecibe: string;
};

export async function prepareDespacho(
  ventaId: string,
): Promise<PrepareDespachoResponse> {
  return apiFetch<PrepareDespachoResponse>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/prepare-despacho`,
  );
}

/**
 * File-type labels for despacho photo uploads. Mirrors the PowerApps
 * `Cbox_TipoFotoDespacho_DM` combobox: each upload is classified so
 * the team can later tell apart a remito from a DNI from a generic
 * retiro photo. None of the types are obligatory; only "at least one
 * file" was required in the legacy app.
 */
export const DESPACHO_FILE_TIPOS = [
  'Remito',
  'DNI Frente',
  'DNI Dorso',
  'Autorización',
  'Fotos del Retiro',
  'Otros',
] as const;
export type DespachoFileTipo = (typeof DESPACHO_FILE_TIPOS)[number];

export type ConfirmarDespachoPayload = {
  recibe: string;
  direccion: string;
  observaciones?: string;
  detalles: Array<{ detalleId: string; serials: string[] }>;
  /** Files staged for upload — one entry per (file, tipo) pair. */
  photos: Array<{ file: File; tipo: DespachoFileTipo }>;
};

/**
 * Side-channel report from the despacho-confirm route on whether the
 * confirmation email made it out. Non-fatal — the despacho commits
 * either way; the UI can use this to nuance the success toast.
 */
export type DespachoEmailResult =
  | { sent: true; to: string; bcc: string | null }
  | {
      sent: false;
      reason: 'no_recipient' | 'pdf_fetch_failed' | 'send_failed';
      error?: string;
    };

export type ConfirmarDespachoResponse =
  | {
      outcome: 'confirmed';
      ventaId: string;
      despachoRowIds: string[];
      photosUploaded: number;
      email: DespachoEmailResult;
    }
  | { outcome: 'already_despachado'; message: string }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'echeq_retenido'; message: string };

// --------------------------------------------------------------------------
// M3 — Puesta Marcha (video uploads)
// --------------------------------------------------------------------------

export type PuestaMarchaSlot = 'instalacion' | 'llama' | 'regulador';
/** All slots the dialog renders, including the optional 4th media picker. */
export type PuestaMarchaPicker = PuestaMarchaSlot | 'multimedia';

export type UploadVideoProgress = {
  loaded: number;
  total: number;
};

export type UploadVideoResponse =
  | {
      outcome: 'uploaded';
      slot: PuestaMarchaPicker;
      driveItemId: string;
      fileName: string;
      size: number;
    }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'already_finalized'; message: string };

/** GET /electrico — derived from the venta's articles + Electrico_ART. */
export type ElectricoCheck = {
  /**
   * `true` only when the venta is *pure electric* (every article has
   * `Electrico_ART = 'SI'`). A venta mixing electric and gas ovens
   * comes back `false` so the gas oven(s) still get their videos.
   */
  electrico: boolean;
  /** Articles in the venta flagged `Electrico_ART = 'SI'`. */
  electricArticulos: string[];
  /** Articles in the venta NOT flagged electric — the gas/non-electric
   *  bucket the combustion videos apply to. Empty in pure-electric
   *  ventas; populated for pure-gas and mixed ventas. */
  gasArticulos: string[];
};

export async function fetchVentaElectrico(ventaId: string): Promise<ElectricoCheck> {
  return apiFetch<ElectricoCheck>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/electrico`,
  );
}

/**
 * Direct-to-Graph chunked upload. The bytes never cross our backend —
 * we ask for a Graph upload-session URL, then PUT the file in 10 MB
 * chunks straight to Microsoft. Bypasses Vercel's 4.5 MB body cap so
 * we can support multi-hundred-MB videos without raising any limits.
 */
type UploadSession = {
  uploadUrl: string;
  expirationDateTime: string | null;
  fileName: string;
  slot: PuestaMarchaPicker;
};

const UPLOAD_CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB — must be a multiple of 320 KiB
const UPLOAD_MAX_RETRIES = 3;

async function requestUploadSession(
  ventaId: string,
  slot: PuestaMarchaPicker,
  fileName: string,
): Promise<UploadSession> {
  return apiFetch<UploadSession>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/puesta-marcha/upload-session`,
    { method: 'POST', body: { slot, fileName } },
  );
}

/** Best-effort cleanup if the user cancels mid-upload. */
export async function cancelPuestaMarchaUploadSession(
  ventaId: string,
  uploadUrl: string,
): Promise<void> {
  try {
    await apiFetch(
      `/api/mantenimiento/${encodeURIComponent(ventaId)}/puesta-marcha/cancel-upload-session`,
      { method: 'POST', body: { uploadUrl } },
    );
  } catch {
    // Graph GCs the session on its own after ~10 min.
  }
}

function putChunk(
  uploadUrl: string,
  chunk: Blob,
  start: number,
  end: number,
  total: number,
  signal?: AbortSignal,
): Promise<{ status: number; body: unknown }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Range', `bytes ${start}-${end}/${total}`);
    // Don't send Authorization — the upload URL carries its own short-
    // lived token. Adding our bearer would actually break the request.
    xhr.responseType = 'json';
    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort);
    xhr.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      resolve({ status: xhr.status, body: xhr.response });
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(new Error('Fallo de red al subir un chunk.'));
    };
    xhr.onabort = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(new Error('Subida cancelada.'));
    };
    xhr.send(chunk);
  });
}

export async function uploadPuestaMarchaVideo(args: {
  ventaId: string;
  slot: PuestaMarchaPicker;
  file: File;
  onProgress?: (p: UploadVideoProgress) => void;
  signal?: AbortSignal;
  /** Optional callback fires the moment we receive the session URL. */
  onSessionUrl?: (url: string) => void;
}): Promise<UploadVideoResponse> {
  const { ventaId, slot, file, onProgress, signal, onSessionUrl } = args;

  let session: UploadSession;
  try {
    session = await requestUploadSession(ventaId, slot, file.name);
    onSessionUrl?.(session.uploadUrl);
  } catch (err) {
    // Surface server-side guard messages (blocked_status / already_finalized)
    // as the structured response the dialog already handles.
    const e = err as { error?: string; message?: string } | undefined;
    if (e?.error === 'blocked_status') {
      return { outcome: 'blocked_status', message: e.message ?? '' };
    }
    if (e?.error === 'already_finalized') {
      return { outcome: 'already_finalized', message: e.message ?? '' };
    }
    throw err;
  }

  const total = file.size;
  let driveItemId = '';
  let fileName = session.fileName;
  let size = total;

  let offset = 0;
  while (offset < total) {
    const end = Math.min(offset + UPLOAD_CHUNK_SIZE, total) - 1;
    const chunk = file.slice(offset, end + 1);

    let lastError: unknown = null;
    let attempt = 0;
    while (attempt < UPLOAD_MAX_RETRIES) {
      try {
        const res = await putChunk(session.uploadUrl, chunk, offset, end, total, signal);
        if (res.status === 202) {
          // Chunk accepted, more to come.
          break;
        }
        if (res.status === 200 || res.status === 201) {
          // Final chunk — the body is the DriveItem.
          const item = res.body as {
            id?: string;
            name?: string;
            size?: number;
          };
          driveItemId = item?.id ?? '';
          if (item?.name) fileName = item.name;
          if (typeof item?.size === 'number') size = item.size;
          break;
        }
        // Retry only on 5xx (per Graph guidance).
        if (res.status >= 500) {
          attempt += 1;
          lastError = new Error(`Graph respondió ${res.status}`);
          await sleep(500 * attempt);
          continue;
        }
        // 4xx — terminal.
        throw new Error(`La subida falló (${res.status}).`);
      } catch (err) {
        attempt += 1;
        lastError = err;
        if (attempt >= UPLOAD_MAX_RETRIES) throw lastError;
        await sleep(500 * attempt);
      }
    }
    offset = end + 1;
    onProgress?.({ loaded: offset, total });
  }

  if (!driveItemId) {
    throw new Error('La subida terminó sin recibir un identificador de archivo.');
  }
  return { outcome: 'uploaded', slot, driveItemId, fileName, size };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export type FinalizePuestaMarchaPayload = {
  uploadedSlots: PuestaMarchaPicker[];
  /** Drive-item ids keyed by slot — server persists them on the venta. */
  slotDriveItemIds?: Partial<Record<PuestaMarchaPicker, string>>;
  observaciones?: string;
};

export type FinalizePuestaMarchaResponse =
  | {
      outcome: 'finalized';
      ventaId: string;
      prevStatus: string;
      electrico: boolean;
      /** false: la venta tiene E-cheqs sin cobrar; se cierra con el último cobro. */
      cerrada?: boolean;
    }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'already_finalized'; message: string }
  | { outcome: 'missing_videos'; missing: PuestaMarchaSlot[]; message: string };

export async function finalizePuestaMarcha(
  ventaId: string,
  payload: FinalizePuestaMarchaPayload,
): Promise<FinalizePuestaMarchaResponse> {
  return apiFetch<FinalizePuestaMarchaResponse>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/puesta-marcha/finalize`,
    { method: 'POST', body: payload },
  );
}

export type AttachMultimediaResponse = {
  outcome: 'attached';
  ventaId: string;
  driveItemId: string;
  previousDriveItemId: string | null;
};

/**
 * Stamps the optional 4th media slot on a venta whose puesta en
 * marcha was already finalized. Pairs with the upload-session flow:
 * client uploads via Graph, then calls this to persist the id.
 */
export async function attachMultimediaToPuestaMarcha(
  ventaId: string,
  driveItemId: string,
): Promise<AttachMultimediaResponse> {
  return apiFetch<AttachMultimediaResponse>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/puesta-marcha/multimedia`,
    { method: 'POST', body: { driveItemId } },
  );
}

// --------------------------------------------------------------------------
// M4 — Certificado de Garantía
// --------------------------------------------------------------------------

export type EmitirCertificadoResponse =
  | {
      outcome: 'issued';
      ventaId: string;
      driveItemId: string;
      fileName: string;
      size: number;
    }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'already_issued'; message: string };

/** Upload a client-generated PDF blob and flip `CertificadoGarantiaCargado_RO='SI'`. */
export async function emitirCertificado(
  ventaId: string,
  pdfBlob: Blob,
  fileName: string,
): Promise<EmitirCertificadoResponse> {
  const form = new FormData();
  form.append('file', pdfBlob, fileName);
  const res = await fetch(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/certificado`,
    {
      method: 'POST',
      body: form,
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Emitir certificado failed: ${res.status} ${text}`);
  }
  return (await res.json()) as EmitirCertificadoResponse;
}

/**
 * Send the previously-emitted Certificado de Garantía to the customer.
 * Server fetches the stored PDF by its drive id; we just hand over the
 * recipient. Mirrors the cotización-email flow.
 */
export type EnviarCertificadoEmailPayload = {
  recipientEmail: string;
  recipientName?: string;
};

export type EnviarCertificadoEmailResponse =
  | { sent: true; to: string; bcc: string | null }
  | {
      sent: false;
      reason: 'no_recipient' | 'no_certificado' | 'pdf_fetch_failed' | 'send_failed';
      error?: string;
    };

export async function enviarCertificadoEmail(
  ventaId: string,
  payload: EnviarCertificadoEmailPayload,
): Promise<EnviarCertificadoEmailResponse> {
  return apiFetch<EnviarCertificadoEmailResponse>(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/certificado/email`,
    {
      method: 'POST',
      body: {
        recipientEmail: payload.recipientEmail,
        recipientName: payload.recipientName ?? '',
      },
    },
  );
}

/**
 * Multipart upload for Confirmar Despacho. Photos go as `photos[]`;
 * the rest of the body rides as a single JSON-serialized `body` field.
 */
export async function confirmarDespacho(
  ventaId: string,
  payload: ConfirmarDespachoPayload,
): Promise<ConfirmarDespachoResponse> {
  const form = new FormData();
  form.append(
    'body',
    JSON.stringify({
      recibe: payload.recibe,
      direccion: payload.direccion,
      observaciones: payload.observaciones ?? '',
      detalles: payload.detalles,
      // Server keeps these parallel to the multipart `photos[]` files.
      fotoTipos: payload.photos.map((p) => p.tipo),
    }),
  );
  for (const p of payload.photos) form.append('photos', p.file);
  const res = await fetch(
    `/api/mantenimiento/${encodeURIComponent(ventaId)}/despacho/confirmar`,
    {
      method: 'POST',
      body: form,
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Confirmar despacho failed: ${res.status} ${text}`);
  }
  return (await res.json()) as ConfirmarDespachoResponse;
}
